import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert } from '@/components/ui/alert'
import { Select } from '@/components/ui/select'
import { vendorsApi } from '@/lib/api/vendors'
import type { Vendor } from '@portal/types'

const FormSchema = z.object({
  name: z
    .string()
    .min(1, 'Vendor name is required')
    .max(80, 'Max 80 characters')
    .regex(/^[a-zA-Z0-9\s]+$/, 'No special characters allowed'),
  nickname: z.string().max(80).optional(),
  gstin: z
    .string()
    .regex(/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/, 'Invalid GSTIN format')
    .optional()
    .or(z.literal('')),
  bankAccount: z.string().regex(/^\d{9,18}$/, 'Account number must be 9–18 digits'),
  bankIfsc: z
    .string()
    .regex(/^[A-Z]{4}0[A-Z0-9]{6}$/, 'Invalid IFSC — e.g. ICIC0000011'),
  upiVpa: z
    .string()
    .regex(/^[a-zA-Z0-9._-]+@[a-zA-Z0-9]+$/, 'Invalid UPI VPA')
    .optional()
    .or(z.literal('')),
  category: z.enum(['SUPPLIER', 'CONTRACTOR', 'UTILITY', 'RENT', 'OTHER']),
})

type FormValues = z.infer<typeof FormSchema>

interface VendorFormProps {
  open: boolean
  onClose: () => void
  onSuccess: (vendor: Vendor) => void
  vendor?: Vendor | null
}

const CATEGORIES = [
  { value: 'SUPPLIER', label: 'Supplier' },
  { value: 'CONTRACTOR', label: 'Contractor' },
  { value: 'UTILITY', label: 'Utility' },
  { value: 'RENT', label: 'Rent' },
  { value: 'OTHER', label: 'Other' },
] as const

export default function VendorForm({ open, onClose, onSuccess, vendor }: VendorFormProps) {
  const isEdit = !!vendor
  const queryClient = useQueryClient()

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      name: '',
      nickname: '',
      gstin: '',
      bankAccount: '',
      bankIfsc: '',
      upiVpa: '',
      category: 'SUPPLIER',
    },
  })

  useEffect(() => {
    if (open && vendor) {
      setValue('name', vendor.name)
      setValue('nickname', vendor.nickname ?? '')
      setValue('gstin', vendor.gstin ?? '')
      setValue('bankAccount', '')
      setValue('bankIfsc', vendor.bankIfsc)
      setValue('upiVpa', vendor.upiVpa ?? '')
      setValue('category', vendor.category as FormValues['category'])
    } else if (open && !vendor) {
      reset()
    }
  }, [open, vendor, reset, setValue])

  const mutation = useMutation({
    mutationFn: (values: FormValues) => {
      const payload = {
        ...values,
        gstin: values.gstin || undefined,
        nickname: values.nickname || undefined,
        upiVpa: values.upiVpa || undefined,
      }
      return isEdit
        ? vendorsApi.update(vendor!.id, payload)
        : vendorsApi.create(payload)
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] })
      onSuccess(res.data!)
      handleClose()
    },
  })

  function handleClose() {
    reset()
    mutation.reset()
    onClose()
  }

  const errorMsg =
    mutation.error instanceof Error
      ? (mutation.error as any)?.error ?? mutation.error.message
      : null

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Vendor' : 'Add Vendor'}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Update vendor details. Changing bank details will clear the ICICI bene registration.'
              : 'Add a new vendor. Bank account is encrypted at rest.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-4 pt-1">
          {errorMsg && (
            <Alert variant="destructive" className="text-sm py-2 px-3">
              {errorMsg}
            </Alert>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 space-y-1">
              <Label htmlFor="name">Vendor Name *</Label>
              <Input
                id="name"
                placeholder="e.g. Acme Supplies"
                {...register('name')}
              />
              {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
            </div>

            <div className="space-y-1">
              <Label htmlFor="nickname">Nickname</Label>
              <Input id="nickname" placeholder="Short name" {...register('nickname')} />
            </div>

            <div className="space-y-1">
              <Label htmlFor="category">Category *</Label>
              <Select
                id="category"
                value={watch('category')}
                onChange={(e) => setValue('category', e.target.value as FormValues['category'])}
              >
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </Select>
              {errors.category && (
                <p className="text-xs text-destructive">{errors.category.message}</p>
              )}
            </div>

            <div className="col-span-2 space-y-1">
              <Label htmlFor="bankAccount">
                Bank Account Number *{isEdit && ' (leave blank to keep existing)'}
              </Label>
              <Input
                id="bankAccount"
                placeholder="9–18 digit account number"
                {...register('bankAccount')}
              />
              {errors.bankAccount && (
                <p className="text-xs text-destructive">{errors.bankAccount.message}</p>
              )}
            </div>

            <div className="col-span-2 space-y-1">
              <Label htmlFor="bankIfsc">IFSC Code *</Label>
              <Input
                id="bankIfsc"
                placeholder="e.g. ICIC0000011"
                className="uppercase"
                {...register('bankIfsc', {
                  setValueAs: (v) => v.toUpperCase(),
                })}
              />
              {errors.bankIfsc && (
                <p className="text-xs text-destructive">{errors.bankIfsc.message}</p>
              )}
              <p className="text-xs text-muted-foreground">
                ICIC0000011 = within ICICI (FT). Other IFSC = NEFT/RTGS.
              </p>
            </div>

            <div className="space-y-1">
              <Label htmlFor="gstin">GSTIN</Label>
              <Input
                id="gstin"
                placeholder="22AAAAA0000A1Z5"
                className="uppercase"
                {...register('gstin', { setValueAs: (v) => v.toUpperCase() })}
              />
              {errors.gstin && <p className="text-xs text-destructive">{errors.gstin.message}</p>}
            </div>

            <div className="space-y-1">
              <Label htmlFor="upiVpa">UPI VPA</Label>
              <Input id="upiVpa" placeholder="vendor@upi" {...register('upiVpa')} />
              {errors.upiVpa && (
                <p className="text-xs text-destructive">{errors.upiVpa.message}</p>
              )}
            </div>
          </div>

          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Vendor'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
